import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable, Subject, forkJoin, from, map, of, tap, throwError } from 'rxjs';
import { HUB, HUB_URL } from 'src/app/enums/hub.enum';
import { environment } from 'src/app/environments/environment';
import { SharedService } from 'src/app/services/shared.service';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {

  private hubConnection_ExportNotification!: signalR.HubConnection;
  private hubConnection_Agenda!: signalR.HubConnection;
  private readonly LOG_PREFIX: string = '[ Signal-R ]';

  constructor(
    public sharedService: SharedService
  ) { }

  buildHubConnection(hub: signalR.HubConnection, url: string) {
    this.sharedService.setToken((accessToken: string) => {
      const options: signalR.IHttpConnectionOptions = {
        accessTokenFactory: () => {
          return accessToken;
        }
      };
      hub = new signalR.HubConnectionBuilder()
        .withUrl(environment.apiUrl + url, options)
        .build()
    })
  }


  buildExportNotificationsHub() {
    // this.buildHubConnection(this.hubConnection_ExportNotification, HUB_URL[HUB.EXPORT])
    this.sharedService.setToken((accessToken: string) => {
      const options: signalR.IHttpConnectionOptions = {
        accessTokenFactory: () => {
          return accessToken;
        }
      };
      this.hubConnection_ExportNotification = new signalR.HubConnectionBuilder()
        .withUrl(environment.apiUrl + HUB_URL[HUB.EXPORT], options)
        .build()
    })
  }

  buildAgendaHub(callback: Function) {
    this.sharedService.setToken((accessToken: string) => {
      const options: signalR.IHttpConnectionOptions = {
        accessTokenFactory: () => {
          return accessToken;
        }
      };
      this.hubConnection_Agenda = new signalR.HubConnectionBuilder()
        .withUrl(environment.apiUrl + HUB_URL[HUB.AGENDA], options)
        .withAutomaticReconnect()
        .withKeepAliveInterval(30000)
        .build();

      this.hubConnection_Agenda.start()
        .then(() => {
          console.log(this.LOG_PREFIX, 'Connection established', HUB.AGENDA);
          callback();
        })
        .catch((error) => {
          console.error('Error connecting to SignalR hub:', error);
          this.hubConnection_Agenda.stop();
        });
    })
  }

  connectAgendaHub(): Observable<any> {
    // return new Observable<void>((observer) => {
    return this.sharedService.getToken().pipe(
      map((accessTokenReponse) => {
        if (accessTokenReponse != null) {
          let accessToken = accessTokenReponse.accessToken;
          const options: signalR.IHttpConnectionOptions = {
            accessTokenFactory: () => {
              return accessToken;
            }
          };
          this.hubConnection_Agenda = new signalR.HubConnectionBuilder()
            .withUrl(environment.apiUrl + HUB_URL[HUB.AGENDA], options)
            .build();
        } else {
          throw new Error('Token not found!');
        }
      }),
      tap(() => {
        return new Observable<void>((observer) => {
          this.hubConnection_Agenda.start()
            .then(() => {
              console.log(this.LOG_PREFIX, 'Connection established', HUB.AGENDA);
              observer.next();
              observer.complete();
            })
            .catch((error) => {
              console.error('Error connecting to SignalR hub:', error);
              observer.error(error);
              this.hubConnection_Agenda.stop();
            });
        })
      }))
    // })
  }

  startExportNotificationsConnection(): Observable<void> {
    if (this.hubConnection_ExportNotification.state !== signalR.HubConnectionState.Connected) {
      return new Observable<void>((observer) => {
        this.hubConnection_ExportNotification
          .start()
          .then(() => {
            console.log(this.LOG_PREFIX, 'Connection established', HUB.EXPORT);
            observer.next();
            observer.complete();
          })
          .catch((error) => {
            console.error('Error connecting to SignalR hub:', error);
            observer.error(error);
            this.hubConnection_ExportNotification.stop();
          });
      });
    } else {
      return of();
    }
  }

  isHubAgendaConnected() {
    return this.hubConnection_ExportNotification && this.hubConnection_ExportNotification.state === signalR.HubConnectionState.Connected;
  }

  onAppointmentCreated(): Observable<any> {
    return new Observable<any>((observer) => {
      this.hubConnection_Agenda.on('AppointmentCreated', (message: any) => {
        observer.next(message);
      });
    });
  }

  onAppointmentUpdated(): Observable<any> {
    return new Observable<any>((observer) => {
      this.hubConnection_Agenda.on('AppointmentUpdated', (message: any) => {
        observer.next(message);
      });
    });
  }

  onAppointmentDeleted(): Observable<any> {
    return new Observable<any>((observer) => {
      this.hubConnection_Agenda.on('AppointmentDeleted', (message: any) => {
        observer.next(message);
      });
    });
  }

  onExportProgressUpdated(): Observable<any> {
    return new Observable<any>((observer) => {
      this.hubConnection_ExportNotification.on('ExportProgressUpdated', (message: any) => {
        observer.next(message);
      });
    });
  }

  receiveMessage(): Observable<string> {
    return new Observable<string>((observer) => {
      this.hubConnection_ExportNotification.on('ReceiveMessage', (message: string) => {
        observer.next(message);
      });
    });
  }

  sendMessage(message: string): void {
    this.hubConnection_ExportNotification.invoke('SendMessage', message);
  }

  toSubscribeEvent(eventId: number) {
    this.hubConnection_Agenda.invoke('SubscribeEvent', eventId)
  }

  stopConnection(hub: signalR.HubConnection) {
    hub.stop();
    console.log(this.LOG_PREFIX, 'Connection stopped');
  }

  stopExportNotificationsConnection() {
    this.stopConnection(this.hubConnection_ExportNotification);
  }

  stopAgendaConnection() {
    this.stopConnection(this.hubConnection_Agenda);
  }

}


/*

 "serverToClientMethods": [
    {
      "methodName": "AppointmentCreated",
      "methodParameters": [
        {
          "parameterName": "appointment",
          "parameterType": "TimedResponse`1<EventAppointmentElementDto>"
        }
      ]
    },
    {
      "methodName": "AppointmentUpdated",
      "methodParameters": [
        {
          "parameterName": "appointment",
          "parameterType": "TimedResponse`1<EventAppointmentElementDto>"
        }
      ]
    },
    {
      "methodName": "AppointmentDeleted",
      "methodParameters": [
        {
          "parameterName": "idAppointment",
          "parameterType": "TimedResponse`1<Int32>"
        }
      ]
    }
  ],
  "clientToServerMethods": [
    {
      "methodName": "SubscribeEvent",
      "methodParameters": [
        {
          "parameterName": "eventId",
          "parameterType": "Int32"
        }
      ]
    },
    {
      "methodName": "UnsubscribeEvent",
      "methodParameters": [
        {
          "parameterName": "eventId",
          "parameterType": "Int32"
        }
      ]
    }
  ]

*/
